import Vue from "vue";
import request from "@/helpers/request";
import * as transform from "@/helpers/transform";
import * as filterUrlBuilder from "@/helpers/filterUrlBuilder";
import { clone } from "lodash";
import { orientationCalc } from "@/helpers/orientation";
import { validCart, validProof } from "@/helpers/validation";
import {
  validateSizesForImageDimensions,
  getSizeOptionGroup
} from "@/helpers/sizeValidator";

export const set = ({ commit }, data) => {
  commit("set", { var: data.var, val: data.val });
}

export const add = ({ commit }, data) => {
  commit("add", { var: data.var, val: data.val });
}

export const remove = ({ commit }, data) => {
  commit("remove", { var: data.var, val: data.val });
}

export const setSelectedOptions = ({ commit, dispatch, state }, data) => {
  // Special actions when a material is selected
  dispatch("handleMaterialSelected", data);

  // Special actions when a size is selected
  dispatch("handleSizeSelected", data);

  // Special actions when a filter is selected
  dispatch("handleFilterSelected", data);

  // Set the selected option on the state
  commit("setSelectedOptions", {
    var: data.var,
    val: data.val
  });

  let optionGroupIndex = state.optionGroups.findIndex(e => e.code == data.val.groupCode);
  if (optionGroupIndex > -1) {
    let optionGroup = state.optionGroups[optionGroupIndex+1];
    if (optionGroup) {
      dispatch('setActiveGroup', optionGroup);
    }
  }

  let variant = transform.getVariant(state.materialSizes, state.selectedOptions)
  if (variant) {
    commit("set", {
      var: "selectedVariant",
      val: variant
    })
  }

  // When an option is selected, make sure the other options selected are still valid.
  let selOpts = clone(state.selectedOptions);
  let plainGroups = clone(state.optionGroups);
  // Loop through currently selected options to make sure they are all still valid
  for (let key in selOpts) {
    selOpts = clone(state.selectedOptions);
    plainGroups = clone(state.optionGroups);
    // Find group that matches the group that each selected option belongs to
    let groupMatch = plainGroups.find(group => group.code == key);
    if (groupMatch) {
      let matchingOption = groupMatch.options.find(
        option => option.optionCode == selOpts[key].optionCode
      );

      if (matchingOption) {
        let valid = true
        if (matchingOption.optionCode === 'none' && matchingOption.isFilter) {
          let filterIndex = plainGroups.findIndex(e => e.isFilter);
          if (!selOpts[plainGroups[filterIndex - 1].code].selected) {
            valid = false
          }
        }

        if (valid) {
          dispatch("pureSelectOptions", {
            var: key,
            val: { ...matchingOption, selected: true },
          });
        }
      }
    }
    else {
      if (!state.selectedOptions[key].isDesignOption) {
        Vue.delete(state.selectedOptions, key);
      }
    }
  }

  // Setup single required options
  dispatch("selectSingleRequiredOptions", state.allOptionGroups);

  // Check if cart is valid
  dispatch("cartValid");
}

export const setActiveGroup = ({ commit }, option_group) => {
  // Set the active option group on the state
  commit("set", {
    var: "activeOptionGroup",
    val: option_group
  })
}

export const setDeactiveGroup = ({ commit }) => {
  // Set the active option group to null
  commit("set", {
    var: "activeOptionGroup",
    val: null
  })
}

export const initOptions = async ({ dispatch, commit }, {productJson, variant_id, from}) => {
  const { id, tags, type, title } = productJson;

  // Set product type
  commit("set", {
    var: "productType",
    val: type
  })

  // Set first material
  let material = transform.getMaterial(tags);
  commit("set", {
    var: "firstMaterial",
    val: material
  })

  let res = await request.post('get_options', { product_id: id, tags })

  let { materialSizes, optionGroups, notes } = res.data

  if (materialSizes.length > 0 && optionGroups.length > 0) {
    commit("set", {
      var: "isDataLoaded",
      val: true
    })
  }

  // Set notes
  commit("set", {
    var: "notes",
    val: notes
  })

  // Set the selected option on the state
  commit("set", {
    var: "materialSizes",
    val: materialSizes
  })

  let initVariant = null;
  if (material != null) {
    let materialSize = materialSizes.find(e => e.name == material);
    if (materialSize) {
      initVariant = materialSize.variants.find(e => e.id == variant_id);
    }
  }

  // Set the original option groups on the state
  commit("set", {
    var: "originalOptionGroups",
    val: optionGroups
  })

  let firstIndex = materialSizes.findIndex(e => e.name == material)
  if (firstIndex == -1) {
    firstIndex = 0
  }

  let materialOptionGroups = transform.initialOptionGroupsFromMaterial(materialSizes, firstIndex)
  let allOptionGroups = [ ...materialOptionGroups, ...optionGroups ]
  commit("set", {
    var: "allOptionGroups",
    val: allOptionGroups
  })

  let filteredOptionGroups = transform.getFilteredOrderGroups(allOptionGroups, material)
  let formatedOptionGroups = transform.initialOptionGroups(filteredOptionGroups)
  commit("set", {
    var: "optionGroups",
    val: formatedOptionGroups
  })

  // Create each option in selectedOptions, and add groupName and optional flag
  dispatch("setupSelectedOptions", { allOptionGroups, initVariant});

  dispatch("isWebToPrint")

  // Set "from"
  commit("set", {
    var: "from",
    val: from
  })

  commit("set", {
    var: "productTitle",
    val: title
  })
}

export const setupSelectedOptions = ({ dispatch, commit, state }, { allOptionGroups, initVariant }) => {
  const firstMaterial = state.firstMaterial

  let optionGroups = clone(state.optionGroups);

  if (optionGroups.length === 1) {
    dispatch("setActiveGroup", optionGroups[0]);
  }
  else {
    for (let [index, group] of optionGroups.entries()) {
      if (firstMaterial) {
        if (index == 1) { dispatch("setActiveGroup", group); }
      }
      else {
        if (index == 0) { dispatch("setActiveGroup", group); }
      }
    }
  }

  // Create each option in selectedOptions
  for (let [index,group] of allOptionGroups.entries()) {
    if (group.code != "DESIGNOPTIONS") {
      // Set default material if option exists
      if (firstMaterial && group.code == 'MATERIAL') {
        let option = group.options.find(e => e.name == firstMaterial);
        if (option) {
          commit("setSelectedOptions", {
            var: group.code,
            val: {
              ...option,
              selected: true
            }
          })
        }
        else {
          commit("setSelectedOptions", {
            var: group.code,
            val: {
              // Has to change with other places with tag: Dingo
              groupCode: group.code,
              groupName: group.name,
              selected: false
            }
          })
        }
      }
      else {
        if (initVariant && (group.code === 'SIZE' || group.code === 'BHSIDES' || group.code === 'CANVASEDGE')) {
          let _optGroup = allOptionGroups.find(e => e.code === group.code)
          let _opt = _optGroup.options.find(e => initVariant[group.code.toLowerCase()] == e.name)
          let selectedOption = { ..._opt, selected: true }
          commit("setSelectedOptions", {
            var: group.code,
            val: selectedOption
          })

          if (group.code === 'SIZE' && _opt) {
            // Special actions when a size is selected
            dispatch("handleSizeSelected", { val: selectedOption });
          }
        }
        else {
          if (group.code === 'FILTER') {
            commit("setSelectedOptions", {
              var: group.code,
              val: {
                // Has to change with other places with tag: Dingo
                groupCode: group.code,
                groupName: group.name,
                name: 'None',
                optionCode: 'none',
                selected: false
              }
            })
          }
          else {
            commit("setSelectedOptions", {
              var: group.code,
              val: {
                // Has to change with other places with tag: Dingo
                groupCode: group.code,
                groupName: group.name,
                selected: false
              }
            })
          }
        }
      }
    }
    else {
      for (let option of group.options) {
        commit("setSelectedOptions", {
          var: option.optionCode,
          val: {
            groupCode: option.optionCode,
            groupName: option.name,
            selected: false,
            isDesignOption: true,
            isDesignNotes: option.isDesignNotes,
            noteRequired: option.noteRequired,
            optional: option.optional
          }
        })
      }
    }
  }
  dispatch("selectSingleRequiredOptions", allOptionGroups);

  // Find selected variant from selected options and material sizes
  let variant = transform.getVariant(state.materialSizes, state.selectedOptions)
  if (variant) {
    commit("set", {
      var: "selectedVariant",
      val: variant
    })
  }
}

export const resetSelectedOption = ({ commit, dispatch }, selectedOption) => {
  commit("setSelectedOptions", {
    var: selectedOption.groupCode,
    val: {
      // Has to change with other places with tag: Dingo
      groupCode: selectedOption.groupCode,
      groupName: selectedOption.groupName,
      selected: false,
      isDesignNotes: !!selectedOption.isDesignNotes,
      noteRequired: !!selectedOption.noteRequired,
      optional: !!selectedOption.optional
    },
  });

  // Check if cart is valid
  dispatch("cartValid");
};

export const selectSingleRequiredOptions = (
  { commit, dispatch },
  optionGroups
) => {
  // If there is only one option and it is required, but it isn't a design option or a master sku group, set it on selectedOptions
  for (let group of optionGroups) {
    if (
      group.options.length === 1 &&
      !group.isDesignOption
    ) {
      commit("setSelectedOptions", {
        var: group.code,
        val: { ...group.options[0], selected: true },
      });
    }
  }
};

export const cartValid = ({ commit, dispatch, state }) => {
  // Check if cart is valid
  let valid = !!(validCart(state.selectedOptions) && state.selectedVariant && state.cropData);
  // TODO: If we make view proof required, send these to validCart too: (state.proofViewed, state.isWebToPrint)
  commit("set", {
    var: "validCart",
    val: valid,
  });
  let proofValid = validProof(state.selectedOptions);
  commit("set", {
    var: "validProof",
    val: proofValid,
  });
}

export const handleMaterialSelected = ({ commit, dispatch, state }, data) => {
  // Special actions when a material is selected
  if (data.val.isMaterial) {
    let materialSizeIndex = state.materialSizes.findIndex(e => e.name == data.val.name)
    if (materialSizeIndex > -1) {
      let materialOptionGroups = transform.initialOptionGroupsFromMaterial(state.materialSizes, materialSizeIndex)
      let allOptionGroups = [ ...materialOptionGroups, ...state.originalOptionGroups ]

      // Validate sizes is there is a size option group
      dispatch("validateSizes", allOptionGroups);

      commit("set", {
        var: "allOptionGroups",
        val: allOptionGroups
      })

      let optionGroups = transform.getFilteredOrderGroups(allOptionGroups, data.val.optionCode)

      let formatedOptionGroups = transform.initialOptionGroups(optionGroups)

      commit("set", {
        var: "optionGroups",
        val: formatedOptionGroups
      })

      dispatch("setupSelectedOptions", { allOptionGroups, initVariant: null});

      commit("set", {
        var: "selectedVariant",
        val: null
      })

      // Check if cart is valid
      dispatch("cartValid");
    }
  }
}

export const pureSelectOptions = ({ dispatch, commit, state }, data) => {
  // Set the selected option on the state without doing other "on select" actions
  commit("setSelectedOptions", {
    var: data.var,
    val: data.val,
  });

  // Check if cart is valid
  dispatch("cartValid");
};

export const handleSizeSelected = ({ commit, state }, data) => {
  // Special actions when a size is selected
  let width = data.val.optionData.Width;
  let height = data.val.optionData.Height;

  if (data.val.isSize) {
    commit("set", { var: "selectedSize", val: data.val });
    // Set width and height according to selected orientation
    let newRatio;
    if (!state.selectedOrientation) {
      // Default to the correct orientation based on ratio
      let orientation = height > width ? "portrait" : "landscape";
      commit("set", { var: "selectedOrientation", val: orientation });
    }
    newRatio = orientationCalc(
      { width: width, height: height },
      state.selectedOrientation
    );

    commit("set", { var: "sizeRatio", val: newRatio });

    let optionGroups = clone(state.optionGroups);
    let canvasIndex = optionGroups.findIndex(e => e.code === 'CANVASEDGE');
    if (canvasIndex > -1) {
      const materialOption = state.selectedOptions.MATERIAL;
      let index = state.materialSizes.findIndex(e => e.name == materialOption.name)
      if (index == -1) {
        index = 0
      }
      const filteredVariants = state.materialSizes[index].variants.filter(e => e.size === data.val.name)
      optionGroups[canvasIndex] = transform.updateCanvasPrices(optionGroups[canvasIndex], filteredVariants);
      commit("set", { var: "optionGroups", val: optionGroups })
    }
  }
}

export const handleFilterSelected = ({ commit, state }, data) => {
  // Special actions when a fitler is selected
  if (data.val.isFilter) {
    let filterParam = data.val.optionData.FilterParameter;
    let image = clone(state.selectedPhoto);
    let imageUrl = clone(state.selectedPhoto.originalImageUrl);

    let filterUrl = filterUrlBuilder.buildUrl(imageUrl, filterParam);

    image.imageUrl = filterUrl;
    commit("set", {
      var: "selectedPhoto",
      val: image,
    });
  }
}

export const validateSizes = ({ state }, optionGroups) => {
  // Validate sizes is there is a size option group
  // Needed when image is uploaded and whenever an option is selected
  if (optionGroups.find(group => group.isSize)) {
    let validSizes = validateSizesForImageDimensions(
      {
        height: state.selectedPhoto.height,
        width: state.selectedPhoto.width,
      },
      optionGroups,
      state.enableLowQualityImage
    );
    getSizeOptionGroup(optionGroups).options = validSizes;
  }
};

export const isWebToPrint = ({ state, commit }) => {
  // Check if product is web to print
  // If it has design notes as an option, it isn't web to print
  let isW2P = true;
  for (let group of state.optionGroups) {
    let designNotesExist = group.options.find(option => option.isDesignNotes);
    if (designNotesExist) {
      isW2P = false;
    }
  }
  commit("set", {
    var: "isWebToPrint",
    val: isW2P,
  });
};

export const closeCustomizer = ({ state, commit, dispatch }) => {
  // Clear customizer options
  // Goto product detail page
  dispatch('clearCustomizer');
};

export const clearCustomizer = ({ state, commit, dispatch }) => {
  // Select Initial Bind
  dispatch('set', { var: 'initialBind', val: null });

  // Select croppie image
  dispatch('set', { var: 'currentCroppieImage', val: null });

  // Select design tab
  dispatch('set', { var: 'selectedTab', val: 0 });

  // Remove selected variant
  dispatch('set', { var: 'selectedVariant', val: null });

  // No add-on selected
  dispatch('set', { var: 'selectedAddOn', val: false });

  // Init selected options
  dispatch('set', { var: 'selectedOptions', val: {} });

  // Make invalid Cart
  dispatch('set', { var: 'validCart', val: false });

  // Make proof false
  dispatch('set', { var: 'validProof',val: false });

  // Init uploaded photos
  dispatch('set', { var: 'uploadedPhotos', val: [] });

  // Remove selected size option
  dispatch('set', { var: 'selectedSize', val: null });

  // Remove selected size option
  dispatch('set', { var: 'selectedPhoto', val: null });

  // Reset enable low quality image
  dispatch('set', { var: 'enableLowQualityImage', val: false });

  // Remove selected filter
  dispatch('set', { var: 'selectedFilter', val: null });

  // Remove active OptionGroup
  dispatch('set', { var: 'activeOptionGroup', val: null });

  // Select portrait orientation
  dispatch('set', { var: 'selectedOrientation', val: 'portrait' });

  // Init quantity
  dispatch('set', { var: 'quantitySelected', val: 1 });

  // Init maxZoom
  dispatch('set', { var: 'maxZoom', val: 10 });

  // Init sizeRatio
  dispatch('set', { var: 'sizeRatio', val: null });

  // Init cropData
  dispatch('set', { var: 'cropData', val: null });
}
